import { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Dropdown, Flex, Modal, Typography } from 'antd';
import { CopyOutlined, SwapOutlined, SyncOutlined } from '@ant-design/icons';
import { ZoomlogiNewColorLogo } from '@/assets/icons/icons';
import SlackLogo from './SlackLogo';
import CustomButton from '@/components/CustomButton';
import CustomInput from '@/components/CustomInput';
import CustomDeleteModal from '@/pages/Modals/CustomDeleteModal';
import './SlackConnectionPanel.scss';
import './TeamCommunication.scss';
import {
  getSlackConnection,
  updateSlackConnection,
} from '@/apis/slackConnections';
import type {
  SlackConnectionAction,
  SlackConnectionStatus,
} from '@/apis/slackConnections';

interface Props {
  teamId: string;
  token: string;
  onChange: () => void;
}

export default function SlackConnectionPanel({
  teamId,
  token,
  onChange,
}: Props) {
  const [status, setStatus] = useState<SlackConnectionStatus | null>(null);
  const [link, setLink] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [reconnecting, setReconnecting] = useState(false);
  const [copied, setCopied] = useState(false);
  const [setupModal, setSetupModal] = useState<'connect' | null>(null);
  const [confirmation, setConfirmation] = useState<
    'test' | 'disconnect' | null
  >(null);

  const reload = useCallback(async () => {
    try {
      const response = await getSlackConnection(token, teamId);
      if (!response.data.data.is_success) throw new Error();
      setStatus(response.data.data.data);
      if (response.data.data.data.pending) {
        setSetupModal(null);
        setLink('');
        setReconnecting(false);
      }
      setError('');
    } catch {
      setError(
        'Slack settings require company administrator access. Try refreshing if you are an administrator.'
      );
    }
  }, [token, teamId]);
  useEffect(() => {
    reload().catch((): void => {});
  }, [reload]);

  const act = async (action: SlackConnectionAction) => {
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const response = await updateSlackConnection(token, teamId, action);
      if (!response.data.data.is_success) throw new Error();
      if (action.action === 'invite') {
        setLink(response.data.data.data.installation_url || '');
        setCopied(false);
      } else {
        setLink('');
        setReconnecting(false);
      }
      if (action.action === 'test')
        setNotice(
          'Test notification queued. Check the selected Slack channel.'
        );
      if (action.action === 'disconnect')
        setNotice(
          'Zoomlogi has stopped sending. Remove the authorization in Slack to revoke it there.'
        );
      await reload();
      onChange();
    } catch {
      setError(
        'Unable to update Slack. Refresh and try again. Setup may be unavailable or the installation link may have expired.'
      );
    } finally {
      setBusy(false);
    }
  };

  const showSetup =
    status?.setup_enabled &&
    !status.pending &&
    !link &&
    (!status.connected || reconnecting);
  const refreshButton = (
    <Button
      disabled={busy}
      onClick={() => {
        reload().catch((): void => {});
      }}
    >
      Refresh Slack status
    </Button>
  );

  return (
    <Flex
      vertical
      gap={12}
      aria-label='Slack connection'
      className='slack-connection fs-exclude rr-block rr-ignore'
    >
      <Flex align='center' gap={12} className='team-communication__card'>
        <span className='team-communication__icon'>
          <SlackLogo />
        </span>
        <Flex vertical gap={2} className='team-communication__content'>
          <Flex align='center' gap={6}>
            <Typography.Text strong>Slack</Typography.Text>
            {status?.connected && (
              <span className='team-communication__synced'>
                <SyncOutlined /> Synced
              </span>
            )}
          </Flex>
          <Typography.Text className='team-communication__hint'>
            {status?.connected
              ? status.channel_name
                ? `${status.channel_name} · ${status.workspace_name}`
                : 'Connected using an existing webhook'
              : status?.pending
                ? 'Channel awaiting confirmation'
                : status?.status === 'reconnect_required'
                  ? 'Slack needs to be reconnected.'
                  : 'Post notifications to a Slack channel'}
          </Typography.Text>
        </Flex>
        {status &&
        (status.connected || status.status === 'reconnect_required') ? (
          <Dropdown
            trigger={['click']}
            menu={{
              items: [
                ...(status.connected
                  ? [{ key: 'test', label: 'Send test notification' }]
                  : []),
                ...(status.setup_enabled && !status.pending
                  ? [{ key: 'change', label: 'Change channel' }]
                  : []),
                { key: 'disconnect', label: 'Disconnect', danger: true },
              ],
              onClick: ({ key }) => {
                if (key === 'change' && !status.pending) {
                  setReconnecting(true);
                  setSetupModal('connect');
                  if (!link) act({ action: 'invite' }).catch((): void => {});
                } else if (key === 'test' || key === 'disconnect')
                  setConfirmation(key);
              },
            }}
          >
            <Button disabled={busy}>Manage</Button>
          </Dropdown>
        ) : (
          <Button
            disabled={busy || !status?.setup_enabled || Boolean(status.pending)}
            onClick={() => {
              setSetupModal('connect');
              if (!link) act({ action: 'invite' }).catch((): void => {});
            }}
          >
            Connect
          </Button>
        )}
      </Flex>
      {status && !status.setup_enabled && (
        <Typography.Text className='team-communication__hint'>
          Slack setup is unavailable. Contact Zoomlogi support for help.
        </Typography.Text>
      )}
      {error && !setupModal && <Alert type='error' message={error} />}
      {notice && <Alert type='info' message={notice} />}
      {!status && !error && (
        <Typography.Text>Loading Slack connection…</Typography.Text>
      )}
      {status && (
        <>
          {status.pending && (
            <Flex vertical gap={12} className='slack-connection__card'>
              <Typography.Text className='slack-connection__destination'>
                They picked {status.pending.channel_name} in the{' '}
                {status.pending.workspace_name} workspace. Confirm it belongs to
                this team before anything is posted.
              </Typography.Text>
              <Flex justify='flex-end' gap={12} wrap>
                <Button
                  disabled={busy}
                  onClick={() => {
                    act({ action: 'cancel' }).catch((): void => {});
                  }}
                >
                  Discard
                </Button>
                <CustomButton
                  text='Confirm channel'
                  className='slack-connection__primary'
                  bgColor='var(--text-primary)'
                  color='var(--surface-container)'
                  height={28}
                  borderRadius='8px'
                  fontSize='12px'
                  fontWeight={600}
                  disabled={busy}
                  onClick={() => {
                    act({
                      action: 'confirm',
                      invite_id: status.pending!.id,
                    }).catch((): void => {});
                  }}
                />
              </Flex>
            </Flex>
          )}
          <Modal
            open={Boolean(setupModal)}
            onCancel={() => {
              setSetupModal(null);
              if (!link) setReconnecting(false);
            }}
            footer={null}
            closable={!busy}
            width={519}
            centered
            title='Zoomlogi notifications wants to post in Slack'
            className='slack-setup-modal communication-setup-modal fs-exclude rr-block rr-ignore'
          >
            <Flex
              justify='center'
              align='center'
              gap={16}
              className='communication-setup-modal__logos'
            >
              <span className='team-communication__icon'>
                <SlackLogo />
              </span>
              <SwapOutlined />
              <span className='team-communication__icon'>
                <ZoomlogiNewColorLogo width={20} height={18} />
              </span>
            </Flex>
            {error && <Alert type='error' message={error} />}
            {status.connected && (
              <Typography.Paragraph className='team-communication__hint'>
                The current connection stays active until you confirm the new
                one.
              </Typography.Paragraph>
            )}
            {busy && <Typography.Text>Preparing Slack setup…</Typography.Text>}
            {showSetup && !busy && (
              <Flex vertical gap={8}>
                <Typography.Text className='team-communication__hint'>
                  Create a link to choose a Slack workspace and channel for this
                  team. You’ll review the connection before notifications start.
                </Typography.Text>
                <CustomButton
                  text={
                    status.connected
                      ? 'Create reconnect link'
                      : 'Create installation link'
                  }
                  className='slack-connection__primary'
                  bgColor='var(--text-primary)'
                  color='var(--surface-container)'
                  height={36}
                  borderRadius='8px'
                  fontSize='12px'
                  fontWeight={600}
                  block
                  disabled={busy}
                  onClick={() => {
                    act({ action: 'invite' }).catch((): void => {});
                  }}
                />
              </Flex>
            )}
            {link && !status.pending && (
              <Flex vertical gap={10} className='slack-connection__card'>
                <Typography.Text className='team-communication__hint'>
                  Send this link to whoever manages the Slack workspace. They
                  pick a channel, then you confirm it here. The link expires in
                  7 days.
                </Typography.Text>
                <Flex gap={8}>
                  <CustomInput
                    aria-label='Slack installation link'
                    className='statsig-no-capture'
                    classNames={{ input: 'statsig-no-capture' }}
                    readOnly
                    value={link}
                  />
                  <CustomButton
                    text={copied ? 'Link copied' : 'Copy link'}
                    icon={<CopyOutlined />}
                    iconPosition='left'
                    gap={6}
                    className='slack-connection__primary'
                    bgColor='var(--text-primary)'
                    color='var(--surface-container)'
                    height={36}
                    borderRadius='8px'
                    fontSize='12px'
                    fontWeight={600}
                    onClick={() => {
                      if (!navigator.clipboard) {
                        setError(
                          'Select and copy the installation link above.'
                        );
                        return;
                      }
                      navigator.clipboard
                        .writeText(link)
                        .then(() => setCopied(true))
                        .catch(() =>
                          setError(
                            'Unable to copy. Select and copy the installation link above.'
                          )
                        );
                    }}
                  />
                </Flex>
                <Typography.Text className='team-communication__hint'>
                  After a channel is selected, refresh below to review and
                  confirm it.
                </Typography.Text>
              </Flex>
            )}
            {status.pending && (
              <Typography.Text>
                Close this window to review and confirm the selected channel.
              </Typography.Text>
            )}
            <Flex justify='flex-end' gap={8} wrap style={{ marginTop: 20 }}>
              {link && !status.pending && (
                <Button
                  type='text'
                  disabled={busy}
                  onClick={() => {
                    act({ action: 'cancel' }).catch((): void => {});
                    setSetupModal(null);
                  }}
                >
                  Cancel installation link
                </Button>
              )}
              {(link || reconnecting || error) && refreshButton}
              <Button
                onClick={() => {
                  setSetupModal(null);
                  if (!link) setReconnecting(false);
                }}
              >
                Close
              </Button>
            </Flex>
          </Modal>
          <Flex gap={8} wrap>
            {!setupModal &&
              (!status.connected ||
                status.status === 'reconnect_required' ||
                (Boolean(link) && reconnecting) ||
                Boolean(status.pending) ||
                Boolean(error)) &&
              refreshButton}
          </Flex>
        </>
      )}
      {!status && error && (
        <Button
          onClick={() => {
            reload().catch((): void => {});
          }}
        >
          Refresh Slack status
        </Button>
      )}
      {confirmation && (
        <CustomDeleteModal
          visible
          onClose={() => setConfirmation(null)}
          onConfirm={() => {
            if (busy) return;
            setConfirmation(null);
            act({ action: confirmation }).catch((): void => {});
          }}
          title={
            confirmation === 'disconnect'
              ? 'Disconnect Slack?'
              : 'Send a test notification?'
          }
          message={
            confirmation === 'disconnect'
              ? 'Stop Slack notifications for this team. To revoke access in Slack too, remove the app authorization in Slack.'
              : 'Send a test notification to the connected Slack channel.'
          }
          cancelText='Cancel'
          confirmText={
            confirmation === 'disconnect' ? 'Disconnect Slack' : 'Send test'
          }
          confirmColor={
            confirmation === 'disconnect'
              ? 'var(--danger)'
              : 'var(--text-primary)'
          }
          disableConfirmBtn={busy}
        />
      )}
    </Flex>
  );
}
